import { Component, forwardRef, Input } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';

@Component({
  selector: 'app-date-picker',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: DatePickerComponent,
    },
  ],
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
})
export class DatePickerComponent implements ControlValueAccessor, Validator {
  @Input() label?: string;
  private disabled: boolean = false;

  isError: boolean = false;

  day: string = '';
  month: string = '';
  year: string = '';

  onChange = (_: any) => {};
  onTouched = () => {};

  constructor() {}

  update() {
    this.onChange(this.getFullDate());
  }

  getFullDate() {
    if (this.day && this.month && this.year) {
      return [
        String("00"+this.day).slice(-2),
        String("00"+this.month).slice(-2),
        this.year,
      ].join('/');
    }

    return null;
  }

  handleInputChange(event: any, maxLength: number, dateType: string) {
    let value = event.target.value;
    if (value.length > maxLength) {
      value = value.slice(0, maxLength);
      event.target.value = value;
    }

    if (dateType === 'day') {
      this.day = value;
    }
    if (dateType === 'month') {
      this.month = value;
    }
    if (dateType === 'year') {
      this.year = value;
    }

    this.update();
  }

  // region interface ControlValueAccessor
  writeValue(obj: any): void {
    if (typeof obj === 'string') {
      const [day, month, year] = obj.split('/');
      this.day = day;
      this.month = month;
      this.year = year;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
  // endregion

  // region interface Validator
  validate(control: AbstractControl): ValidationErrors | null {
    if (!this.isValidDay() || !this.isValidMonth() || !this.isValidYear()) {
      this.isError = true;
      return {
        invalid: true,
      };
    }

    const fullDate = this.getFullDate();
    if (!fullDate) {
      this.isError = true;
      return {
        required: true,
      };
    }

    if (!this.isValidDate(fullDate)) {
      this.isError = true;
      return {
        invalid: true,
      };
    }

    this.isError = false;
    return null;
  }
  // endregion

  isValidDay() {
    if (!this.day) {
      return true;
    }

    const day = parseInt(this.day, 10);
    return day >= 1 && day <= 31;
  }
  isValidMonth() {
    if (!this.month) {
      return true;
    }

    const month = parseInt(this.month, 10);
    return month >= 1 && month <= 12;
  }
  isValidYear() {
    if (!this.year) {
      return true;
    }

    const year = parseInt(this.year, 10);
    return year >= 1 && year <= 3000;
  }

  // Modified stackoverflow link: https://stackoverflow.com/a/6178341
  // Validates that the input string is a valid date formatted as "dd/mm/yyyy"
  isValidDate(dateString: string) {
    // First check for the pattern
    if (!/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
      return false;
    }

    // Parse the date parts to integers
    const parts = dateString.split('/');
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    // Check the ranges of day and month
    if (
      day <= 0 ||
      day > 31 ||
      month <= 0 ||
      month > 12 ||
      year <= 0 ||
      year > 3000
    ) {
      return false;
    }

    const monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    // Adjust for leap years
    if (year % 400 == 0 || (year % 100 != 0 && year % 4 == 0))
      monthLength[1] = 29;

    // Check the range of the day
    return day > 0 && day <= monthLength[month - 1];
  }
}
